/**
 * Task List Component
 *
 * Displays a list of tasks with priority indicators, due dates, projects, and time estimates.
 * Supports "soft completion" where tasks stay visible for a period before disappearing,
 * allowing users to undo accidental completions.
 *
 * Design system: Neural Mono — card borders, left accent bar, monospace meta, data-id ghost.
 */

import { useRef, useState, useLayoutEffect, useMemo } from 'react';
import { Check, Clock, CalendarDays, Loader2, MessageSquare, ListChecks, Undo2, Sparkles, CheckCircle2, Share2 } from 'lucide-react';
import { format, isPast, isToday, isTomorrow, isValid, parseISO } from 'date-fns';
import clsx from 'clsx';
import type { Task, Project } from '../types';
import { getProjectColor } from '../utils/projectColors';
import { formatCompletedDate } from '../utils/dates';
import { useDecomposeSuggestions } from '../hooks/useDecomposeSuggestions';
import type { DecomposeSuggestion } from '../api/ai';

/** Format snoozeUntil UTC timestamp to "Wakes up Apr 10" */
function formatWakeUp(snoozeUntil: string): string {
  const d = new Date(snoozeUntil);
  return `Wakes up ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

/**
 * Renders as many labels as fit in the available width, then shows "+N" for the rest.
 * Uses ResizeObserver so it reacts when the panel opens/closes.
 */
function LabelOverflow({ labels, showAsCompleted }: { labels: string[]; showAsCompleted: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(labels.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const compute = () => {
      const containerWidth = container.clientWidth;
      const GAP = 4; // gap-1 = 4px
      const BADGE_WIDTH = 28; // reserve space for "+10"
      // Monospace 9px: char ≈ 5.4px, px-1.5 padding = 12px each side total
      const labelWidth = (label: string) => label.length * 5.4 + 12 + GAP;

      let used = 0;
      let count = 0;
      for (let i = 0; i < labels.length; i++) {
        const w = labelWidth(labels[i]);
        const moreAfter = i < labels.length - 1;
        const budget = moreAfter ? containerWidth - BADGE_WIDTH : containerWidth;
        if (used + w > budget) break;
        used += w;
        count++;
      }
      setVisibleCount(Math.max(1, count));
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [labels]);

  const labelClass = showAsCompleted
    ? 'bg-surface-base-light dark:bg-surface-raised-dark text-text-tertiary-light dark:text-text-tertiary-dark'
    : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300';

  return (
    <div ref={containerRef} className="flex items-center gap-1 min-w-0 overflow-hidden flex-1">
      {labels.slice(0, visibleCount).map((label) => (
        <span key={label} className={clsx('shrink-0 px-1.5 py-px rounded font-mono text-xs md:text-[9px] font-medium tracking-wide', labelClass)}>
          {label}
        </span>
      ))}
      {visibleCount < labels.length && (
        <span className="shrink-0 font-mono text-xs md:text-[9px] text-text-secondary-light dark:text-text-secondary-dark opacity-40">
          +{labels.length - visibleCount}
        </span>
      )}
    </div>
  );
}

/** Compact estimate: "2 HOURS" → "2h", "90 MINUTES" → "1h 30m", "30 MINUTES" → "30m" */
function formatEstimate(raw: string): string {
  const m = raw.match(/^([\d.]+)\s*(hours?|hrs?|h|minutes?|mins?|m)$/i);
  if (!m) return raw.toLowerCase();
  const val = parseFloat(m[1]);
  const unit = m[2].toLowerCase();
  if (unit.startsWith('h')) {
    const hrs = Math.floor(val);
    const mins = Math.round((val - hrs) * 60);
    return mins > 0 ? `${hrs}h${mins}m` : `${hrs}h`;
  }
  if (val >= 60) {
    const hrs = Math.floor(val / 60);
    const mins = Math.round(val % 60);
    return mins > 0 ? `${hrs}h${mins}m` : `${hrs}h`;
  }
  return `${val}m`;
}

interface TaskListProps {
  tasks: Task[];
  isLoading: boolean;
  onTaskSelect: (task: Task) => void;
  selectedTaskId?: string;
  onTaskComplete?: (taskId: string) => void;
  onTaskUndo?: (taskId: string) => void;
  onTaskReopen?: (taskId: string) => void;
  /** Set of task IDs that are pending completion (in undo window) */
  pendingCompletions?: Set<string>;
  /** Project lookup map - passed from parent to ensure data is loaded */
  projectsMap?: Record<string, Project>;
  /** Callback when user clicks the decompose suggestion banner */
  onTaskSuggestDecompose?: (task: Task) => void;
}

// Priority colors for checkbox border — heat map: red → orange → yellow → gray
const priorityBorderColors: Record<number, string> = {
  1: 'border-priority-1',
  2: 'border-priority-2',
  3: 'border-priority-3',
  4: 'border-gray-300 dark:border-gray-600',
};

// Priority colors for hover checkmark icon
const priorityCheckColors: Record<number, string> = {
  1: 'text-priority-1',
  2: 'text-priority-2',
  3: 'text-priority-3',
  4: 'text-gray-400 dark:text-gray-400',
};

// Priority filled colors for completed state
const priorityFilledColors: Record<number, string> = {
  1: 'border-priority-1 bg-[#db1c42]',
  2: 'border-priority-2 bg-[#d96b15]',
  3: 'border-priority-3 bg-[#fbce57]',
  4: 'border-gray-400 bg-gray-400',
};

// Priority badge shown in meta row — Neural Mono data aesthetic
const priorityBadge: Record<number, { label: string; className: string }> = {
  1: { label: 'P1', className: 'text-priority-1 bg-[#db1c42]/15' },
  2: { label: 'P2', className: 'text-priority-2 bg-[#d96b15]/15' },
  3: { label: 'P3', className: 'text-priority-3 bg-[#fbce57]/20' },
  4: { label: 'P4', className: 'text-gray-500 dark:text-gray-400 bg-gray-500/10' },
};

/**
 * Format a 24-hour HH:MM time for compact display (e.g. "5:00 PM").
 * Returns empty string if the time string is falsy.
 */
function formatCompactTime(time: string): string {
  const m = time.match(/^(\d{2}):(\d{2})/);
  if (!m) return '';
  const hour = parseInt(m[1] ?? '0', 10);
  const minute = parseInt(m[2] ?? '0', 10);
  const d = new Date(2000, 0, 1, hour, minute);
  return ` ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
}

function formatDueDate(due: Task['due']): { text: string; isOverdue: boolean; isUrgent: boolean } {
  if (!due?.date) {
    return { text: '', isOverdue: false, isUrgent: false };
  }

  const dueDate = parseISO(due.date);
  if (!isValid(dueDate)) {
    return { text: '', isOverdue: false, isUrgent: false };
  }
  const isOverdue = isPast(dueDate) && !isToday(dueDate);

  // Extract time portion from due.datetime if present
  const timeStr = due.datetime
    ? (() => {
        const m = due.datetime.match(/T(\d{2}:\d{2})/);
        return m ? formatCompactTime(m[1] ?? '') : '';
      })()
    : '';

  if (isToday(dueDate)) {
    return { text: `Today${timeStr}`, isOverdue: false, isUrgent: true };
  }
  if (isTomorrow(dueDate)) {
    return { text: `Tomorrow${timeStr}`, isOverdue: false, isUrgent: false };
  }
  if (isOverdue) {
    return { text: `${format(dueDate, 'MMM d')}${timeStr}`, isOverdue: true, isUrgent: true };
  }

  return { text: `${format(dueDate, 'MMM d')}${timeStr}`, isOverdue: false, isUrgent: false };
}

export function TaskList({
  tasks,
  isLoading,
  onTaskSelect,
  selectedTaskId,
  onTaskComplete,
  onTaskUndo,
  onTaskReopen,
  pendingCompletions = new Set(),
  projectsMap = {},
  onTaskSuggestDecompose,
}: TaskListProps) {
  const recentlyCompletedRef = useRef<Set<string>>(new Set());
  const { suggestions } = useDecomposeSuggestions();

  // Build a Map<taskId, DecomposeSuggestion> for O(1) lookups per row
  const suggestionsByTaskId = useMemo(() => {
    const map = new Map<string, DecomposeSuggestion>();
    for (const s of suggestions) {
      map.set(s.taskId, s);
    }
    return map;
  }, [suggestions]);

  const handleCheckboxClick = (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    e.preventDefault();

    const isPendingCompletion = pendingCompletions.has(task.id);
    const wasRecentlyCompleted = recentlyCompletedRef.current.has(task.id);

    if (wasRecentlyCompleted) return;

    if (isPendingCompletion) {
      onTaskUndo?.(task.id);
    } else if (task.isCompleted && onTaskReopen) {
      onTaskReopen(task.id);
    } else if (!task.isCompleted && onTaskComplete) {
      recentlyCompletedRef.current.add(task.id);
      setTimeout(() => { recentlyCompletedRef.current.delete(task.id); }, 500);
      onTaskComplete(task.id);
    }
  };

  const handleUndoClick = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    onTaskUndo?.(taskId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-text-secondary-light dark:text-text-secondary-dark">
        <Check className="w-12 h-12 text-text-tertiary-light dark:text-text-tertiary-dark mb-3" />
        <p className="text-sm font-mono font-medium">No tasks in this view</p>
        <p className="text-xs font-mono mt-1 opacity-60">Try a different view or add a task</p>
      </div>
    );
  }

  return (
    <div className="md:flex md:flex-col md:gap-2">
      {tasks.map((task) => {
        const dueInfo = formatDueDate(task.due);
        const isSelected = task.id === selectedTaskId;
        const project = task.projectId ? projectsMap[task.projectId] : null;
        const projectColor = getProjectColor(project?.color);
        const isPendingCompletion = pendingCompletions.has(task.id);
        const showAsCompleted = task.isCompleted || isPendingCompletion;
        const badge = priorityBadge[task.priority] ?? priorityBadge[4];
        const hasSuggestion = suggestionsByTaskId.has(task.id);

        return (
          <div
            key={task.id}
            data-testid="task-row"
            onClick={() => onTaskSelect(task)}
            className={clsx(
              // Neural Mono card base
              'group/item relative w-full pl-1 md:pl-5 pr-4 md:pr-3 flex items-start gap-3 md:gap-2.5',
              'text-left cursor-pointer transition-all duration-150',
              // Mobile: no card, divider line; Desktop: card style
              'py-3.5 md:py-2.5 border-b border-border-hover-light dark:border-border-hover-dark',
              'md:border md:rounded-lg md:mb-0',
              // Desktop: card background
              'md:bg-surface-raised-light md:dark:bg-surface-raised-dark',
              showAsCompleted && 'opacity-60',
              isSelected
                ? [
                    'bg-surface-raised-light/50 dark:bg-surface-raised-dark/50 md:bg-surface-raised-light md:dark:bg-surface-raised-dark',
                    'md:border-border-hover-light md:dark:border-border-hover-dark',
                    'md:shadow-card',
                  ]
                : [
                    'md:border-border-subtle-light md:dark:border-border-subtle-dark',
                    'hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark',
                    'md:hover:border-border-hover-light md:dark:hover:border-border-hover-dark',
                    'md:hover:-translate-y-px md:hover:shadow-card',
                  ]
            )}
          >
            {/* Left accent bar — teal on hover/selected */}
            <div
              data-testid="task-accent-bar"
              className={clsx(
                'absolute left-0 md:left-1.5 top-3 bottom-3 w-0.5 rounded-sm transition-all duration-150',
                isSelected
                  ? 'bg-primary-500'
                  : 'bg-transparent group-hover/item:bg-primary-400 group-hover/item:[box-shadow:0_0_6px_var(--color-primary-400)]'
              )}
            />

            {/* Priority Checkbox — square, Neural Mono style */}
            {/* Touch target wrapper (invisible, 44px on mobile) */}
            <button
              onClick={(e) => handleCheckboxClick(e, task)}
              className="group/cb mt-0.5 shrink-0 p-2.5 md:p-0 -m-2.5 md:m-0 flex items-center justify-center cursor-pointer"
              aria-label={
                isPendingCompletion ? 'Undo completion' :
                task.isCompleted ? 'Mark as incomplete' :
                'Mark as complete'
              }
            >
              {/* Visible checkbox stays small */}
              <div
                data-testid="task-checkbox-box"
                className={clsx(
                  'w-5 h-5 rounded border-2',
                  'flex items-center justify-center transition-all duration-150',
                  showAsCompleted
                    ? priorityFilledColors[task.priority] ?? priorityFilledColors[4]
                    : priorityBorderColors[task.priority] ?? priorityBorderColors[4],
                  'group-hover/cb:scale-110',
                  task.priority === 1 && !showAsCompleted && 'priority-urgent',
                )}
              >
                {showAsCompleted ? (
                  <Check
                    className={clsx(
                      'w-3 h-3',
                      task.priority === 3 ? 'text-yellow-900' : 'text-white'
                    )}
                    strokeWidth={3}
                  />
                ) : (
                  <Check
                    className={clsx(
                      'w-3 h-3 opacity-0 group-hover/cb:opacity-60 transition-opacity duration-75',
                      priorityCheckColors[task.priority] ?? priorityCheckColors[4]
                    )}
                    strokeWidth={3}
                  />
                )}
              </div>
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0 flex items-start gap-3 overflow-hidden">
              {/* Left: Title + meta */}
              <div className="flex-1 min-w-0 overflow-hidden">
                {/* Task title — monospace, Neural Mono aesthetic */}
                <p className={clsx(
                  'text-sm font-mono truncate leading-snug mt-[2px]',
                  showAsCompleted
                    ? 'text-text-tertiary-light dark:text-text-tertiary-dark line-through font-medium'
                    : isSelected
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-text-primary-light dark:text-text-primary-dark font-medium'
                )}>
                  {task.content}
                </p>

                {/* Meta row — natural flow, consistent gap: badge | due | project | time | labels */}
                <div className="flex items-center gap-3 mt-1 overflow-hidden">

                  {/* Priority badge */}
                  <span className={clsx(
                    'shrink-0 inline-block px-1.5 py-px rounded font-mono text-xs md:text-[9px] font-bold tracking-widest uppercase',
                    badge.className
                  )}>
                    {badge.label}
                  </span>

                  {/* Due date */}
                  <span className={clsx(
                    'shrink-0 flex items-center gap-0.5 font-mono text-xs md:text-[10px] tabular-nums',
                    showAsCompleted
                      ? 'text-text-tertiary-light dark:text-text-tertiary-dark opacity-40'
                      : dueInfo.isOverdue
                        ? 'text-red-500 dark:text-red-400'
                        : dueInfo.isUrgent
                          ? 'text-green-700 dark:text-green-400'
                          : dueInfo.text
                            ? 'text-text-secondary-light dark:text-text-secondary-dark'
                            : 'text-text-secondary-light dark:text-text-secondary-dark opacity-30'
                  )}>
                    <CalendarDays className="w-3.5 h-3.5 md:w-2.5 md:h-2.5 shrink-0" />
                    <span>{dueInfo.text || 'whenever'}</span>
                  </span>

                  {/* Snooze wake-up indicator */}
                  {task.snoozeUntil && (
                    <span className="shrink-0 flex items-center gap-0.5 font-mono text-xs md:text-[10px] text-amber-600 dark:text-amber-400 tabular-nums">
                      <Clock className="w-3.5 h-3.5 md:w-2.5 md:h-2.5 shrink-0" />
                      <span>{formatWakeUp(task.snoozeUntil)}</span>
                    </span>
                  )}

                  {/* Google Calendar badge */}
                  {task.gcalEventId && !task.isCompleted && (
                    <span title="Scheduled on Google Calendar" className="shrink-0 text-blue-500 dark:text-blue-400">
                      <CalendarDays className="w-3.5 h-3.5 md:w-2.5 md:h-2.5" />
                    </span>
                  )}

                  {/* Completed date indicator */}
                  {task.isCompleted && task.completedAt && (
                    <span className="shrink-0 flex items-center gap-0.5 font-mono text-xs md:text-[10px] text-green-600 dark:text-green-400 tabular-nums">
                      <CheckCircle2 className="w-3.5 h-3.5 md:w-2.5 md:h-2.5 shrink-0" />
                      <span>Completed {formatCompletedDate(task.completedAt)}</span>
                    </span>
                  )}

                  {/* Project */}
                  {project && (
                    <span className={clsx(
                      'shrink-0 flex items-center gap-1 font-mono text-xs md:text-[10px] uppercase tracking-wide',
                      showAsCompleted
                        ? 'text-text-tertiary-light dark:text-text-tertiary-dark'
                        : 'text-text-secondary-light dark:text-text-secondary-dark'
                    )}>
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: projectColor }} />
                      <span className="truncate max-w-[80px]">{project.name}</span>
                    </span>
                  )}

                  {/* Time estimate */}
                  {task.metadata?.timeEstimate && (
                    <span className={clsx(
                      'shrink-0 flex items-center gap-0.5 font-mono text-xs md:text-[10px]',
                      showAsCompleted
                        ? 'text-text-tertiary-light dark:text-text-tertiary-dark'
                        : 'text-text-secondary-light dark:text-text-secondary-dark'
                    )}>
                      <Clock className="w-3.5 h-3.5 md:w-2.5 md:h-2.5 shrink-0" />
                      <span>{formatEstimate(task.metadata.timeEstimate)}</span>
                    </span>
                  )}

                  {/* Labels — fills remaining space, clips to fit with +N */}
                  {task.labels && task.labels.length > 0 && (
                    <LabelOverflow labels={task.labels} showAsCompleted={showAsCompleted} />
                  )}

                  {/* Shared list source badge */}
                  {task.sharedListName && task.sharedListId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.dispatchEvent(
                          new CustomEvent('danny-navigate-view', {
                            detail: { viewSlug: `shared:${task.sharedListId}` },
                          })
                        );
                      }}
                      className="inline-flex shrink-0 items-center gap-1 text-[10px] font-mono text-text-tertiary-light dark:text-text-tertiary-dark hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      title={`From shared list: ${task.sharedListName}`}
                    >
                      <Share2 className="w-2.5 h-2.5" />
                      {task.sharedListName}
                    </button>
                  )}

                  {/* Subtask / comment indicators */}
                  {(() => {
                    const commentCount = task.comments?.length ?? 0;
                    const subtasks = tasks.filter(t => t.parentId === task.id);
                    const subtaskCount = subtasks.length;
                    const completedSubtasks = subtasks.filter(t => t.isCompleted).length;
                    if (commentCount === 0 && subtaskCount === 0) return null;

                    return (
                      <div className={clsx(
                        'flex items-center gap-2 font-mono text-xs md:text-[10px]',
                        showAsCompleted
                          ? 'text-text-tertiary-light dark:text-text-tertiary-dark'
                          : 'text-text-secondary-light dark:text-text-secondary-dark'
                      )}>
                        {commentCount > 0 && (
                          <span className="flex items-center gap-0.5">
                            <MessageSquare className="w-3.5 h-3.5 md:w-2.5 md:h-2.5" />
                            {commentCount}
                          </span>
                        )}
                        {subtaskCount > 0 && (
                          <span className="flex items-center gap-0.5">
                            <ListChecks className="w-3.5 h-3.5 md:w-2.5 md:h-2.5" />
                            {completedSubtasks}/{subtaskCount}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Decompose suggestion banner */}
                {hasSuggestion && !showAsCompleted && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTaskSuggestDecompose?.(task);
                    }}
                    className="flex items-center gap-1 mt-1 text-xs font-mono text-primary-600 dark:text-primary-400 hover:underline cursor-pointer"
                  >
                    <Sparkles className="w-3 h-3" />
                    Danny has suggestions for breaking this down
                  </button>
                )}
              </div>

              {/* Right: Undo only when pending */}
              {isPendingCompletion && (
                <div className="shrink-0 mt-0.5">
                  <button
                    onClick={(e) => handleUndoClick(e, task.id)}
                    className="inline-flex items-center gap-1 font-mono text-xs md:text-[10px] uppercase tracking-wide font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors cursor-pointer"
                    aria-label="Undo completion"
                  >
                    <Undo2 className="w-3 h-3" />
                    Undo
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
